import { Node } from '@xyflow/react';
import { DeviceData } from '../../DevicesTab';
import { MonitoringZoneDto } from '@/lib/api/types.gen';
import { QueryClient } from '@tanstack/react-query';
import { updateSpeaker } from '@/lib/api/monitoring/speakers';
import { updateRelay } from '@/lib/api/monitoring/relays';
import { LinkingMode } from '../types';

interface DropDeviceData {
  deviceType: 'camera' | 'multi-camera' | 'speaker' | 'relay';
  deviceId: string;
  deviceName?: string;
  selectedCameraIds?: string[];
  componentGroupId?: string;
  relatedComponentGroups?: string[];
  enabled?: boolean;
}

interface ZoneDimensions {
  width: number;
  height: number;
}

interface ZoneInfo {
  id: string;
  data: {
    zoneId: string;
    zoneName: string;
    isDefault?: boolean;
    zone?: MonitoringZoneDto;
    zoneDimensions?: ZoneDimensions;
  };
  dimensions: ZoneDimensions;
}

interface CreateZoneNodesParams {
  allZones: ZoneInfo[];
  positions: { x: number; y: number }[];
  companyId: string;
  siteId: string;
  onDeviceDrop: (deviceData: any, targetZoneId: string) => Promise<void>;
  onRemoveEverything?: (zoneId: string) => void;
  totalCameraCount?: number;
}

export const createZoneNodes = ({ 
  allZones, 
  positions, 
  companyId, 
  siteId, 
  onDeviceDrop,
  onRemoveEverything,
  totalCameraCount 
}: CreateZoneNodesParams): Node[] => {
  return allZones.map((zoneInfo, index) => ({
    id: zoneInfo.id,
    type: 'zone',
    position: positions[index],
    data: {
      ...zoneInfo.data,
      companyId,
      siteId,
      onDeviceDrop,
      onRemoveEverything,
      totalCameraCount,
    },
    style: { 
      width: zoneInfo.dimensions.width, 
      height: zoneInfo.dimensions.height,
    },
    draggable: false,
    selectable: true,
    zIndex: 0,
  }));
};

interface CameraNodeData {
  deviceId: string;
  deviceName: string;
  assetName?: string;
  nvrName?: string;
  camera: DeviceData;
  siteId: string;
  companyId: string;
  onStopMonitoring: (deviceId: string) => void;
  onEditGrids: (deviceId: string) => void;
  hoveredCameraId: string | null;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onCameraClick?: () => void;
  isLinkingMode?: boolean;
  isLinkedInMode?: boolean;
  onDeviceDrop: (deviceData: DropDeviceData, cameraId: string) => Promise<void>;
}

interface CameraNodeBaseData {
  siteId: string;
  companyId: string;
  onStopMonitoring: (deviceId: string) => Promise<void>;
  onEditGrids: (deviceId: string) => void;
  hoveredCameraId: string | null;
  onMouseEnter: (cameraId: string) => void;
  onMouseLeave: () => void;
  onCameraClick?: (cameraId: string) => void;
  isLinkingMode?: boolean;
  linkingMode?: LinkingMode;
  onDeviceDrop: (deviceData: DropDeviceData, cameraId: string) => Promise<void>;
  allSpeakers?: any[];
  allRelays?: any[];
}

interface CreateCameraNodesParams {
  cameras: DeviceData[];
  zonePosition: { x: number; y: number };
  nodeData: CameraNodeBaseData;
  totalCameraCount: number;
}

export const createCameraNodes = ({
  cameras,
  zonePosition,
  nodeData,
  totalCameraCount,
}: CreateCameraNodesParams): Node[] => {
  return cameras.map((camera, cameraIndex) => {
    const totalCameras = cameras.length;
    let cameraX, cameraY;
    
    if (totalCameras > 6) {
      const camerasPerRow = Math.ceil(totalCameras / 2);
      const isTopRow = cameraIndex < camerasPerRow;
      const positionInRow = isTopRow ? cameraIndex : cameraIndex - camerasPerRow;
      
      cameraX = zonePosition.x + 20 + (positionInRow * 180);
      cameraY = zonePosition.y + (isTopRow ? 50 : 530);
    } else {
      cameraX = zonePosition.x + 20 + (cameraIndex * 180);
      cameraY = zonePosition.y + 50;
    }
    
    return {
      id: `camera-${camera.id}`,
      type: 'camera',
      position: { x: cameraX, y: cameraY },
      data: {
        ...nodeData,
        deviceId: camera.id,
        deviceName: camera.name,
        assetName: camera.assetName,
        nvrName: camera.nvrName,
        camera: camera,
        totalCameraCount: totalCameraCount,
        onMouseEnter: () => nodeData.onMouseEnter?.(camera.id),
        onCameraClick: nodeData.onCameraClick ? () => nodeData.onCameraClick?.(camera.id) : undefined,
        isLinkedInMode: nodeData.isLinkingMode && camera.originalDevice?.id && nodeData.linkingMode?.pendingLinks?.includes(camera.originalDevice.id),
      },
      draggable: false,
      selectable: true,
      zIndex: 500,
    };
  });
};

interface SpeakerNodeBaseData {
  onStopMonitoring: (deviceId: string) => void;
  onEditGrids: (deviceId: string) => void;
  hoveredCameraId: string | null;
  onLinkToAllCameras: (deviceId: string, deviceType: 'speaker' | 'relay') => void;
  onEnterLinkingMode: (deviceId: string, deviceType: 'speaker' | 'relay') => void;
  isCurrentLinkingDevice?: boolean;
  onRemoveFromZone: (deviceId: string) => Promise<void>;
}

interface CreateSpeakerNodesParams {
  speakers: DeviceData[];
  zonePosition: { x: number; y: number };
  baseNodeData: SpeakerNodeBaseData;
}

export const createSpeakerNodes = ({
  speakers,
  zonePosition,
  baseNodeData
}: CreateSpeakerNodesParams): Node[] => {
  return speakers.map((speaker, speakerIndex) => ({
    id: `speaker-${speaker.id}`,
    type: 'speaker',
    position: {
      x: zonePosition.x + 20 + (speakerIndex * 180),
      y: zonePosition.y + 270,
    },
    data: {
      ...baseNodeData,
      deviceId: speaker.id,
      deviceName: speaker.name,
      assetName: speaker.assetName,
      nvrName: speaker.nvrName,
      nvrId: speaker.nvrId,
      relatedComponentGroups: speaker.relatedComponentGroups,
      associatedCameraIds: speaker.associatedCameraIds,
      associatedCameraId: speaker.associatedCameraId,
    },
    draggable: false,
    selectable: true,
    zIndex: 500,
  }));
};

interface RelayNodeBaseData {
  onStopMonitoring: (deviceId: string) => void;
  onEditGrids: (deviceId: string) => void;
  hoveredCameraId: string | null;
  onLinkToAllCameras: (deviceId: string, deviceType: 'speaker' | 'relay') => void;
  onEnterLinkingMode: (deviceId: string, deviceType: 'speaker' | 'relay') => void;
  isCurrentLinkingDevice?: boolean;
  onRemoveFromZone: (deviceId: string) => Promise<void>;
}

interface CreateRelayNodesParams {
  relays: DeviceData[];
  zonePosition: { x: number; y: number };
  baseNodeData: RelayNodeBaseData;
}

export const createRelayNodes = ({
  relays,
  zonePosition,
  baseNodeData
}: CreateRelayNodesParams): Node[] => {
  return relays.map((relay, relayIndex) => ({
    id: `relay-${relay.id}`,
    type: 'relay',
    position: {
      x: zonePosition.x + 20 + (relayIndex * 180),
      y: zonePosition.y + 400,
    },
    data: {
      ...baseNodeData,
      deviceId: relay.id,
      deviceName: relay.name,
      assetName: relay.assetName,
      nvrName: relay.nvrName,
      nvrId: relay.nvrId,
      relatedComponentGroups: relay.relatedComponentGroups,
      associatedCameraIds: relay.associatedCameraIds,
      associatedCameraId: relay.associatedCameraId,
    },
    draggable: false,
    selectable: true,
    zIndex: 500,
  }));
};

// Factory function to create camera device drop handler
export const createCameraDeviceDropHandler = (
  queryClient: QueryClient,
  companyId: string,
  siteId: string,
  monitoredCameras?: DeviceData[]
) => {
  return async (deviceData: DropDeviceData, cameraId: string) => {
    if (deviceData.deviceType === 'speaker' || deviceData.deviceType === 'relay') {
      try {
        // Get the target camera's original device ID
        const targetCamera = monitoredCameras?.find(cam => cam.id === cameraId);
        const originalDeviceId = targetCamera?.originalDevice?.id;
        
        if (originalDeviceId) {
          if (deviceData.deviceType === 'speaker') {
            await updateSpeaker({
              id: deviceData.deviceId,
              name: deviceData.deviceName || '',
              componentGroupId: deviceData.componentGroupId || deviceData.deviceId,
              relatedComponentGroups: [...new Set([...(deviceData.relatedComponentGroups || []), originalDeviceId])],
            });
          } else if (deviceData.deviceType === 'relay') {
            await updateRelay({
              id: deviceData.deviceId,
              name: deviceData.deviceName || '',
              componentGroupId: deviceData.componentGroupId || deviceData.deviceId,
              relatedComponentGroups: [...new Set([...(deviceData.relatedComponentGroups || []), originalDeviceId])],
            });
          }
          
          // Trigger refresh
          queryClient.invalidateQueries({ 
            queryKey: ['siteDevices', { companyId, projectId: siteId }],
            refetchType: 'all'
          });
        }
      } catch (error) {
        console.error('📷 Error linking device to camera:', error);
      }
    }
  };
};

// Factory function to create speaker/relay remove handler  
export const createDeviceRemoveHandler = (
  queryClient: QueryClient,
  companyId: string,
  siteId: string,
  deviceType: 'speaker' | 'relay'
) => {
  return async (deviceId: string, deviceName: string, componentGroupId?: string, enabled?: boolean) => {
    try {
      if (deviceType === 'speaker') {
        await updateSpeaker({
          id: deviceId,
          name: deviceName,
          componentGroupId: componentGroupId || deviceId,
          relatedComponentGroups: [], // Clear all camera links
          enabled: enabled !== false
        });
      } else if (deviceType === 'relay') {
        await updateRelay({
          id: deviceId,
          name: deviceName,
          componentGroupId: componentGroupId || deviceId,
          relatedComponentGroups: [], // Clear all camera links
          enabled: enabled !== false
        });
      }
      
      // Trigger refresh
      queryClient.invalidateQueries({ 
        queryKey: ['siteDevices', { companyId, projectId: siteId }],
        refetchType: 'all'
      });
    } catch (error) {
      console.error('🔌 Error removing device from zone:', error);
    }
  };
};